/* import __COLOCATED_TEMPLATE__ from './more-filters-item.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { DataType } from 'embercom/objects/inbox/conversation-attribute-descriptor';
import { tracked } from 'tracked-built-ins';
import {
  type BooleanComparison,
  type Comparison,
} from 'embercom/objects/inbox/search/predicate-group';
import type IntlService from 'embercom/services/intl';
import { type CvdaDateTimeItem, type CvdaListItem, type MoreFiltersItem } from '../search2';
import type Inbox2AssigneeSearch from 'embercom/services/inbox2-assignee-search';
import type AdminWithPermissions from 'embercom/objects/inbox/admin-with-permissions';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import type Session from 'embercom/services/session';
// @ts-ignore - predicates is a classic Ember addon
import { mapBackendOperatorsToComparisonOptions } from 'predicates/models/predicate';

export interface ComparisonOption {
  value: string;
  label: string;
}

interface Args {
  filterItem: MoreFiltersItem;
  onValueUpdated: (
    item: MoreFiltersItem,
    value: string | CvdaDateTimeItem['value'],
    comparison?: Comparison,
  ) => void;
  dropdownClass?: string;
  onRemove: (item: MoreFiltersItem) => void;
}

interface Signature {
  Element: HTMLElement;
  Args: Args;
}

export default class MoreFiltersItemComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare inbox2AssigneeSearch: Inbox2AssigneeSearch;
  @service declare session: Session;

  @tracked isHovered = false;
  @tracked skipAutoSave = false;
  @tracked textItemSelected: string = this.initialTextItemSelectedValue();
  @tracked booleanSelectedValue: BooleanComparison = this.initialBooleanSelectedValue();
  @tracked listItemSelected: CvdaListItem | undefined = this.initialListItemSelectedValue();
  @tracked dateTimeItemSelected: CvdaDateTimeItem = this.initalDateTimeItemSelectedValue();
  @tracked numericItemSelected?: string = this.initialNumericItemSelectedValue();
  @tracked selectedAdmin: AdminSummary | undefined = this.initialSelectedAdminValue();
  @tracked comparisonSelectedValue: Comparison = this.comparisonSelected();
  elementId = `more-filters-item-${this.sanitizeElementId(this.args.filterItem.attribute.id)}`;
  @tracked listComparisonSelected: Comparison = this.initialListComparisonValue();
  @tracked textComparisonSelected: Comparison = this.initialTextComparisonValue();

  private decimalIntegerRegex = new RegExp(/^-?\d*\.?\d*$/);
  private wholeNumberRegex = new RegExp(/^-?\d*$/);

  constructor(owner: unknown, args: Args) {
    super(owner, args);
  }

  get isPopoverOpen(): boolean {
    return this.args.filterItem.value === undefined;
  }

  // Sanitize ID to be a valid CSS selector by replacing dots and other invalid characters
  private sanitizeElementId(id: string | number): string {
    return String(id).replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  get icon(): InterfaceIconName | undefined {
    return this.args.filterItem.attribute.icon as InterfaceIconName | undefined;
  }

  get attributeId(): string {
    return `${this.args.filterItem.attribute.id}`;
  }

  get attributeType(): DataType {
    return this.args.filterItem.attribute.type as DataType;
  }

  get filterValue() {
    switch (this.args.filterItem.attribute.type) {
      case DataType.Boolean:
        return this.labelValue(this.booleanSelectedValue);
      case DataType.List:
        return this.labelValueWithComparison(
          this.listItemSelected?.text,
          this.listComparisonSelected,
        );
      case DataType.Datetime:
        return this.dateTimeItemSelected.value.value
          ? `: ${this.dateTimeComparisonLabel} ${this.dateTimeItemSelected.value.value} ${this.dateTimeUnitLabel}`
          : this.labelValue();
      case DataType.Integer:
      case DataType.Decimal:
        return this.labelValue(this.numericItemSelected);
      default:
        return this.labelValueWithComparison(this.textItemSelected, this.textComparisonSelected);
    }
  }

  private labelValueWithComparison(value?: string, comparison?: Comparison): string {
    if (!value) {
      return '';
    }
    let comparisonLabel = this.getComparisonLabel(comparison);
    return comparisonLabel ? ` ${comparisonLabel} ${value}` : `: ${value}`;
  }

  private getComparisonLabel(comparison?: Comparison | string): string {
    if (!comparison || comparison === 'eq') {
      return '';
    }

    // Map comparison operators to translation keys and get translated labels
    // This mirrors the logic from predicates library
    let comparisonTranslations: Record<string, string> = {
      ne: this.intl.t('common.predicates.comparison.ne'),
      in: this.intl.t('common.predicates.comparison.in'),
      nin: this.intl.t('common.predicates.comparison.nin'),
      gt: this.intl.t('common.predicates.comparison.gt'),
      lt: this.intl.t('common.predicates.comparison.lt'),
      contains: this.intl.t('common.predicates.comparison.contains'),
      known: this.intl.t('common.predicates.comparison.known'),
      unknown: this.intl.t('common.predicates.comparison.unknown'),
    };

    return comparisonTranslations[String(comparison)] || '';
  }

  private labelValue(value?: string): string {
    return value ? `: ${value}` : '';
  }

  private initialTextItemSelectedValue(): string {
    if (this.showTextOptions && this.args.filterItem.value) {
      return this.args.filterItem.value as string;
    }
    return '';
  }

  private initialSelectedAdminValue(): AdminSummary | undefined {
    if (this.showCreatedByAdminFilterOptions && this.args.filterItem.value) {
      return this.selectedTeammateFromSearch || undefined;
    }
    return undefined;
  }

  private initialBooleanSelectedValue(): BooleanComparison {
    if (this.showBooleanOptions && this.args.filterItem.value) {
      return this.args.filterItem.value as BooleanComparison;
    }
    return 'true';
  }

  private initialListItemSelectedValue(): CvdaListItem | undefined {
    if (this.showListOptions && this.args.filterItem.value) {
      return this.listItems.find((item: CvdaListItem) => item.id === this.args.filterItem.value);
    }
    return this.listItems[0] || undefined;
  }

  private initalDateTimeItemSelectedValue(): CvdaDateTimeItem {
    if (this.showDateTimeOptions && this.args.filterItem.value) {
      return {
        value: this.args.filterItem.value as CvdaDateTimeItem['value'],
        comparison: this.args.filterItem.comparison || 'lt',
      };
    }
    return {
      value: {
        value: '',
        unit: 'minutes',
        direction: 'past',
      },
      comparison: 'lt',
    };
  }

  private initialNumericItemSelectedValue(): string | undefined {
    if (this.showNumericOptions && this.args.filterItem.value) {
      return this.args.filterItem.value as string;
    }
    return undefined;
  }
  private comparisonSelected(): Comparison {
    if (this.args.filterItem && this.args.filterItem.comparison) {
      return this.args.filterItem.comparison;
    }
    return 'eq';
  }

  private initialListComparisonValue(): Comparison {
    if (this.showListOptions && this.args.filterItem.comparison) {
      return this.args.filterItem.comparison;
    }
    // Default to 'eq' if no comparison operators specified, or use the first available
    let options = this.listComparisonOptions;
    return options && options.length > 0 ? (options[0].value as Comparison) : 'eq';
  }

  private initialTextComparisonValue(): Comparison {
    if (this.showTextOptions && this.args.filterItem.comparison) {
      return this.args.filterItem.comparison;
    }
    // Default to 'eq' if no comparison operators specified, or use the first available
    let options = this.textComparisonOptions;
    return options && options.length > 0 ? (options[0].value as Comparison) : 'eq';
  }

  get listComparisonOptions(): ComparisonOption[] | null {
    if (!this.showListOptions) {
      return null;
    }
    // Only use unified comparison operators if feature flag is enabled
    // Guard against accessing workspace before it's initialized (in tests)
    if (!this.session?.workspace?.canUseFeature?.('team-inbox-unified-comparison-operators')) {
      return null;
    }
    let attribute = this.args.filterItem.attribute;
    let operators = 'comparisonOperators' in attribute ? attribute.comparisonOperators : null;
    return mapBackendOperatorsToComparisonOptions(operators, this.intl);
  }

  get textComparisonOptions(): ComparisonOption[] | null {
    if (!this.showTextOptions) {
      return null;
    }
    // Only use unified comparison operators if feature flag is enabled
    // Guard against accessing workspace before it's initialized (in tests)
    if (!this.session?.workspace?.canUseFeature?.('team-inbox-unified-comparison-operators')) {
      return null;
    }
    let attribute = this.args.filterItem.attribute;
    let operators = 'comparisonOperators' in attribute ? attribute.comparisonOperators : null;
    return mapBackendOperatorsToComparisonOptions(operators, this.intl);
  }

  get dateTimeComparisonLabel() {
    if (this.dateTimeItemSelected.comparison === 'lt') {
      return this.intl.t('inbox.search.filters.more-filters.more-than');
    }
    return this.intl.t('inbox.search.filters.more-filters.less-than');
  }

  get dateTimeUnitLabel() {
    let unit = this.dateTimeItemSelected.value.unit;
    let value = this.dateTimeItemSelected.value.value || 0;
    return this.intl.t(`inbox.search.filters.more-filters.${unit}`, { value });
  }

  get isDisabled(): boolean {
    switch (this.args.filterItem.attribute.type) {
      case DataType.Boolean:
        return false;
      case DataType.List:
        return false;
      case DataType.Datetime:
        return !this.isDateTimeItemValid;
      case DataType.Integer:
      case DataType.Decimal:
        return !this.isNumericItemValid;
      default:
        return !this.isTextItemValid;
    }
  }

  get selectedTeammateFromSearch() {
    return this.inbox2AssigneeSearch.searchableAdmins.find(
      (admin) => admin.data.id.toString() === this.args.filterItem?.value,
    )?.data as AdminWithPermissions | undefined;
  }

  @action onTextItemSelect(value: string) {
    this.textItemSelected = value;
  }

  @action onAdminSelected(admin: AdminSummary | undefined) {
    this.selectedAdmin = admin;
    this.args.onValueUpdated(this.args.filterItem, admin?.id.toString() || '');
  }

  @action onDoneClickAndClose(popover: any) {
    this.savePredicate();
    this.skipAutoSave = true;
    popover.hide();
  }

  @action onRemoveAndClose(popover: any) {
    this.args.onRemove(this.args.filterItem);
    this.skipAutoSave = true;
    popover.hide();
  }

  private savePredicate() {
    switch (this.args.filterItem.attribute.type) {
      case DataType.Boolean:
        this.args.onValueUpdated(this.args.filterItem, this.booleanSelectedValue);
        break;
      case DataType.List:
        this.args.onValueUpdated(
          this.args.filterItem,
          this.listItemSelected!.id,
          this.listComparisonSelected,
        );
        break;
      case DataType.Datetime:
        if (this.isDateTimeItemValid) {
          this.args.onValueUpdated(
            this.args.filterItem,
            this.dateTimeItemSelected.value,
            this.dateTimeItemSelected.comparison,
          );
        }
        break;
      case DataType.Integer:
      case DataType.Decimal:
        if (this.isNumericItemValid) {
          this.args.onValueUpdated(
            this.args.filterItem,
            this.numericItemSelected!,
            this.args.filterItem.comparison,
          );
        }
        break;
      default:
        if (this.isTextItemValid) {
          this.args.onValueUpdated(
            this.args.filterItem,
            this.textItemSelected,
            this.textComparisonSelected,
          );
        }
    }
  }

  @action onBooleanSelect(value: BooleanComparison) {
    this.booleanSelectedValue = value;
  }

  get showBooleanOptions() {
    return this.args.filterItem.attribute.type === DataType.Boolean;
  }

  get showListOptions() {
    return this.args.filterItem.attribute.type === DataType.List;
  }

  get showDateTimeOptions() {
    return this.args.filterItem.attribute.type === DataType.Datetime;
  }

  get showNumericOptions() {
    return (
      this.args.filterItem.attribute.type === DataType.Integer ||
      this.args.filterItem.attribute.type === DataType.Decimal
    );
  }

  get showCreatedByAdminFilterOptions() {
    return this.args.filterItem.attribute.type === DataType.AdminId;
  }

  get adminFilterPlaceholder() {
    return 'inbox.search.filters.created-by.search-placeholder';
  }

  get adminFilterButtonText() {
    return this.intl.t('inbox.search.filters.created-by.opener-blank');
  }

  get shouldHideUnassignedOption() {
    // Hide unassigned option for admin filters like Created By
    return true;
  }

  get showTextOptions() {
    return (
      !this.showBooleanOptions &&
      !this.showListOptions &&
      !this.showDateTimeOptions &&
      !this.showNumericOptions
    );
  }

  get listItems(): CvdaListItem[] {
    if (!this.showListOptions) {
      return [];
    }

    let listOptions = this.args.filterItem.attribute.listOptions;
    if (!listOptions || !Array.isArray(listOptions)) {
      return [];
    }

    let result: CvdaListItem[] = [];
    for (let item of listOptions) {
      if (!item.archived) {
        result.push({ text: item.label, id: String(item.id) });
      }
    }
    return result;
  }

  @action onListItemSelect(item: CvdaListItem) {
    this.listItemSelected = { text: item.text, id: item.id };
  }

  @action onListComparisonSelect(comparison: Comparison) {
    this.listComparisonSelected = comparison;
    // Don't close dropdown when changing comparison
  }

  @action onTextComparisonSelect(comparison: Comparison) {
    this.textComparisonSelected = comparison;
    // Don't close dropdown when changing comparison
  }

  @action onRemove(event?: Event) {
    event?.stopPropagation();
    this.args.onRemove(this.args.filterItem);
  }

  @action onRemoveWithoutEvent() {
    this.args.onRemove(this.args.filterItem);
  }

  @action onPopoverHide() {
    // Skip auto-save if the user explicitly clicked Done or Remove
    if (this.skipAutoSave) {
      this.skipAutoSave = false;
      return;
    }

    // When the popover closes (via clickout), auto-save the value if it's valid
    if (this.showTextOptions && this.textItemSelected && !this.isDisabled) {
      this.args.onValueUpdated(
        this.args.filterItem,
        this.textItemSelected,
        this.textComparisonSelected,
      );
    } else if (this.showBooleanOptions && !this.isDisabled) {
      this.args.onValueUpdated(this.args.filterItem, this.booleanSelectedValue);
    } else if (this.showListOptions && this.listItemSelected && !this.isDisabled) {
      this.args.onValueUpdated(
        this.args.filterItem,
        this.listItemSelected.id,
        this.listComparisonSelected,
      );
    } else if (
      this.showDateTimeOptions &&
      this.dateTimeItemSelected.value.value &&
      !this.isDisabled
    ) {
      this.args.onValueUpdated(
        this.args.filterItem,
        this.dateTimeItemSelected.value,
        this.dateTimeItemSelected.comparison,
      );
    } else if (this.showNumericOptions && this.numericItemSelected && !this.isDisabled) {
      this.args.onValueUpdated(
        this.args.filterItem,
        this.numericItemSelected,
        this.comparisonSelectedValue,
      );
    }
  }

  @action onComparisonSelect(comparison: CvdaDateTimeItem['comparison']) {
    this.dateTimeItemSelected = {
      ...this.dateTimeItemSelected,
      comparison,
    };
  }
  @action onFiltersItemComparisonSelect(comparison: Comparison) {
    this.args.filterItem.comparison = comparison;
  }

  @action onValueSelect(value: string) {
    this.dateTimeItemSelected = {
      ...this.dateTimeItemSelected,
      value: {
        ...this.dateTimeItemSelected.value,
        value,
      },
    };
  }

  @action onUnitSelect(unit: CvdaDateTimeItem['value']['unit']) {
    this.dateTimeItemSelected = {
      ...this.dateTimeItemSelected,
      value: {
        ...this.dateTimeItemSelected.value,
        unit,
      },
    };
  }

  @action onNumericItemSelect(value: string) {
    this.numericItemSelected = value;
  }

  get isNumericItemValid(): boolean {
    if (!this.numericItemSelected) {
      return false;
    }

    if (this.args.filterItem.attribute.type === DataType.Integer) {
      return this.wholeNumberRegex.test(this.numericItemSelected);
    } else if (this.args.filterItem.attribute.type === DataType.Decimal) {
      return this.decimalIntegerRegex.test(this.numericItemSelected);
    }
    return true;
  }

  get isTextItemValid(): boolean {
    return !!this.textItemSelected.trim();
  }

  get isDateTimeItemValid(): boolean {
    if (
      !this.dateTimeItemSelected.value.value ||
      !this.wholeNumberRegex.test(this.dateTimeItemSelected.value.value)
    ) {
      return false;
    }
    return true;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Search::MoreFiltersItem': typeof MoreFiltersItemComponent;
  }
}
